import React, { useState, useEffect } from 'react';
import { Discount, DiscountFilters, DiscountStatus, DiscountType } from '../../types/discount';
import { discountService } from '../../services/discountService';

interface DiscountListingProps {
  discounts: Discount[];
  onEdit: (discount: Discount) => void;
  onDelete: (discountId: string) => void;
  onStatusChange: (discountId: string, action: 'activate' | 'deactivate' | 'expire') => void;
  onViewStats: (discount: Discount) => void;
  isLoading?: boolean;
}

const DiscountListing: React.FC<DiscountListingProps> = ({
  discounts,
  onEdit,
  onDelete,
  onStatusChange,
  onViewStats,
  isLoading = false
}) => {
  const [filters, setFilters] = useState<DiscountFilters>({});
  const [sortBy, setSortBy] = useState<'createdAt' | 'validUntil' | 'usageCount' | 'name'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filteredDiscounts, setFilteredDiscounts] = useState<Discount[]>(discounts);

  useEffect(() => {
    let filtered = [...discounts];

    // Apply filters
    if (filters.status) {
      filtered = filtered.filter(discount => discount.status === filters.status);
    }
    if (filters.type) {
      filtered = filtered.filter(discount => discount.type === filters.type);
    }
    if (filters.isPublic !== undefined) {
      filtered = filtered.filter(discount => discount.isPublic === filters.isPublic);
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(discount => 
        discount.name.toLowerCase().includes(searchLower) ||
        discount.code.toLowerCase().includes(searchLower) ||
        discount.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'validUntil':
          aValue = new Date(a.validUntil);
          bValue = new Date(b.validUntil);
          break;
        case 'usageCount':
          aValue = a.usageCount;
          bValue = b.usageCount;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        default:
          aValue = a.createdAt;
          bValue = b.createdAt;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredDiscounts(filtered);
  }, [discounts, filters, sortBy, sortOrder]);

  const handleFilterChange = (key: keyof DiscountFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const formatDate = (date: Date | string): string => {
    return new Date(date).toLocaleDateString();
  };

  const isExpiringSoon = (discount: Discount): boolean => {
    return discountService.isDiscountExpiringSoon(discount, 7);
  };

  const getUsagePercentage = (discount: Discount): number => {
    return discountService.calculateUsagePercentage(discount);
  };

  const getBudgetPercentage = (discount: Discount): number => {
    return discountService.calculateBudgetPercentage(discount);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search discounts..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value={DiscountStatus.ACTIVE}>Active</option>
              <option value={DiscountStatus.INACTIVE}>Inactive</option>
              <option value={DiscountStatus.EXPIRED}>Expired</option>
              <option value={DiscountStatus.USED_UP}>Used Up</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={filters.type || ''}
              onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value={DiscountType.PERCENTAGE}>Percentage</option>
              <option value={DiscountType.FIXED_AMOUNT}>Fixed Amount</option>
              <option value={DiscountType.FREE_DAYS}>Free Days</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Visibility
            </label>
            <select
              value={filters.isPublic !== undefined ? filters.isPublic.toString() : ''}
              onChange={(e) => handleFilterChange('isPublic', e.target.value === '' ? undefined : e.target.value === 'true')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Public</option>
              <option value="false">Private</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Name
                  {sortBy === 'name' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type & Value
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('validUntil')}
              >
                <div className="flex items-center">
                  Valid Until
                  {sortBy === 'validUntil' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('usageCount')}
              >
                <div className="flex items-center">
                  Usage
                  {sortBy === 'usageCount' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDiscounts.map((discount) => (
              <tr key={discount._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {discount.name}
                      {isExpiringSoon(discount) && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Expiring Soon
                        </span>
                      )}
                    </div>
                    {discount.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {discount.description}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                    {discount.code}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div className="font-medium">
                      {discountService.formatDiscountValue(discount)}
                    </div>
                    <div className="text-gray-500 capitalize">
                      {discount.type.replace('_', ' ')}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${discountService.getDiscountStatusColor(discount.status)}`}>
                    {discount.status.replace('_', ' ').toUpperCase()}
                  </span>
                  {!discount.isPublic && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Private
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(discount.validUntil)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div>
                      {discount.usageCount}
                      {discount.usageLimit > 0 && ` / ${discount.usageLimit}`}
                    </div>
                    {discount.usageLimit > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${Math.min(getUsagePercentage(discount), 100)}%` }}
                        ></div>
                      </div>
                    )}
                    {discount.budgetLimit > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Budget: ${discount.budgetUsed.toFixed(2)} / ${discount.budgetLimit.toFixed(2)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(discount)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onViewStats(discount)}
                      className="text-green-600 hover:text-green-900"
                    >
                      Stats
                    </button>
                    
                    {discount.status === DiscountStatus.ACTIVE && (
                      <button
                        onClick={() => onStatusChange(discount._id, 'deactivate')}
                        className="text-orange-600 hover:text-orange-900"
                      >
                        Deactivate
                      </button>
                    )}
                    
                    {discount.status === DiscountStatus.INACTIVE && (
                      <button
                        onClick={() => onStatusChange(discount._id, 'activate')}
                        className="text-green-600 hover:text-green-900"
                      >
                        Activate
                      </button>
                    )}
                    
                    {(discount.status === DiscountStatus.ACTIVE || discount.status === DiscountStatus.INACTIVE) && (
                      <button
                        onClick={() => onStatusChange(discount._id, 'expire')}
                        className="text-red-600 hover:text-red-900"
                      >
                        Expire
                      </button>
                    )}
                    
                    <button
                      onClick={() => onDelete(discount._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredDiscounts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">
            {discounts.length === 0 ? 'No discounts found.' : 'No discounts match your filters.'}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountListing;